const sv = {
  energyTitle: "Energi, rörelsemängd och systemmassa",
  dopplerTitle: "Samma ljusstråle i två inertialsystem",
  modes: "Modelläge",
  particle: "En partikel",
  pair: "Två partiklar",
  threshold: "Produktionströskel",
  beta: "β = u/c",
  momentum: "p₁c/ε₀",
  secondMomentum: "p₂c/ε₀",
  kinetic: "Tillförd kinetisk energi KΣ/ε₀",
  productMass: "Viloenergi per ny partikel / ε₀",
  fixed: "Fast mål",
  collider: "Motriktade strålar",
  configuration: "Strålgeometri",
  cm: "Välj masscentrumsystemet",
  cmOutside: "Masscentrumsystemet ligger utanför reglagets β-intervall.",
  units:
    "c = {one}. ε₀ är en inkommande partikels viloenergi. Axlar och tabell visar energier i enheter av ε₀.",
  energySvg: "Energi–rörelsemängdsdiagram",
  energyDesc:
    "Energi uppåt och signerad rörelsemängd gånger c åt höger. A och B adderas till Σ i S. Σ′ beskriver samma system i S′. Hyperbeln genom totalsumman har oförändrad systemmassa.",
  legend:
    "Heldragen kurva: systemets masshyperbel. Streckad: en partikels masshyperbel. Prickade räta linjer: masslösa gränsen. A och B: inkommande partiklar; Σ: summan i S; Σ′: samma summa i S′. Diagrammet är ingen bana. Skalan anpassas till punkterna.",
  frame: "System",
  energy: "E/ε₀",
  pc: "pₓc/ε₀",
  invariant: "W²/ε₀²",
  mass: "W/ε₀ = Mc²/ε₀",
  particleNote: "För en partikel är W = ε₀. K = E − ε₀; ändra p och jämför sedan systemen med β.",
  pairNote:
    "Två partiklar med samma viloenergi. Ändra deras rörelsemängder oberoende: systemmassan beror på deras relativa rörelse. Om de fastnar utan strålning har slutkroppen viloenergin W.",
  kineticReadout: "K/ε₀",
  classical: "Kklassisk/ε₀ vid samma hastighet",
  error: "Relativt fel i Kklassisk",
  velocity: "v/c",
  forceParallel: "F∥/(ma∥)",
  forceTransverse: "F⊥/(ma⊥)",
  thresholdNote:
    "Två lika inkommande partiklar skapar ytterligare ett par. Vid tröskeln saknar slutpartiklarna relativ rörelse i masscentrumsystemet. Jämförelsen nedan håller den sammanlagda tillförda kinetiska energin fast.",
  requiredCurve:
    "Gul streckad hyperbel: minsta systemmassa för slutprodukterna. På tröskeln sammanfaller den med systemets heldragna kurva.",
  required: "Wmin/ε₀",
  fixedW: "W/ε₀ med fast mål",
  colliderW: "W/ε₀ med motriktade strålar",
  fixedThreshold: "KΣ,min/ε₀ med fast mål",
  colliderThreshold: "KΣ,min/ε₀ med motriktade strålar",
  cmKinetic: "Masscentrumrörelsens energi i S: (EΣ − W)/ε₀",
  allowed: "Kinematiskt tillåten",
  forbidden: "Under tröskeln",
  thresholdCaution:
    "Tillåten betyder att energi och rörelsemängd räcker; modellen beräknar ingen reaktionssannolikhet. Vid exakt tröskel är det tillgängliga fasrummet noll.",
  angle: "Strålens vinkel mot +x",
  angleFrame: "Vinkeln ställs in i",
  source: "Källans vilosystem S",
  receiver: "Mottagarens vilosystem S′",
  sourceAngle: "θ i S",
  receiverAngle: "θ′ i S′",
  ratio: "f′/f₀",
  transverseSource: "Tvärgående i S",
  transverseReceiver: "Tvärgående i S′",
  dopplerSvg: "Aberration och dopplerfaktor",
  dopplerDesc:
    "Två separata riktningscirklar visar samma ljusstråles riktning i källans respektive mottagarens vilosystem. Pilarnas längd är normerad; frekvenskvoten visas i tabellen.",
  dopplerNote:
    "S är källans vilosystem; S′ rör sig med β längs +x. Vinklarna avser ljusets utbredningsriktning, inte riktningen från mottagaren tillbaka till källan. Pilarna visar riktning, inte frekvens eller bana.",
  dopplerFormula: String.raw`$f'/f_0=\gamma_u(1-\beta\cos\theta)=1/[\gamma_u(1+\beta\cos\theta')]$. Tvärgående i S ger $\gamma_u$; tvärgående i S′ ger $1/\gamma_u$.`,
  nullNorm: "(ω′² − kₓ′² − kᵧ′²)/ω₀²",
};
const en: Record<keyof typeof sv, string> = {
  energyTitle: "Energy, momentum and system mass",
  dopplerTitle: "The same light ray in two inertial frames",
  modes: "Model mode",
  particle: "One particle",
  pair: "Two particles",
  threshold: "Production threshold",
  beta: "β = u/c",
  momentum: "p₁c/ε₀",
  secondMomentum: "p₂c/ε₀",
  kinetic: "Supplied kinetic energy KΣ/ε₀",
  productMass: "Rest energy per new particle / ε₀",
  fixed: "Fixed target",
  collider: "Colliding beams",
  configuration: "Beam geometry",
  cm: "Select centre-of-momentum frame",
  cmOutside: "The centre-of-momentum frame lies outside the β slider range.",
  units:
    "c = {one}. ε₀ is the rest energy of one incoming particle. Axes and table use ε₀ as the energy unit.",
  energySvg: "Energy–momentum diagram",
  energyDesc:
    "Energy upward, signed momentum times c rightward. A and B add to Σ in S. Σ′ describes the same system in S′. The hyperbola through the total has constant invariant mass.",
  legend:
    "Solid curve: system mass hyperbola. Dashed: one-particle mass hyperbola. Dotted straight lines: massless limit. A and B: incoming particles; Σ: sum in S; Σ′: same sum in S′. This is not a trajectory. The scale adapts to the points.",
  frame: "Frame",
  energy: "E/ε₀",
  pc: "pₓc/ε₀",
  invariant: "W²/ε₀²",
  mass: "W/ε₀ = Mc²/ε₀",
  particleNote: "For one particle W = ε₀. K = E − ε₀; vary p, then compare frames with β.",
  pairNote:
    "Two particles with equal rest energy. Change their momenta independently: system mass depends on relative motion. If they stick without radiation, the final body's rest energy is W.",
  kineticReadout: "K/ε₀",
  classical: "Classical K/ε₀ at the same velocity",
  error: "Relative error in classical K",
  velocity: "v/c",
  forceParallel: "F∥/(ma∥)",
  forceTransverse: "F⊥/(ma⊥)",
  thresholdNote:
    "Two identical incoming particles create an additional pair. At threshold the final particles have no relative motion in the centre-of-momentum frame. The comparison below holds total supplied kinetic energy fixed.",
  requiredCurve:
    "Yellow dashed hyperbola: minimum system mass for the products. At threshold it coincides with the solid system curve.",
  required: "Wmin/ε₀",
  fixedW: "W/ε₀, fixed target",
  colliderW: "W/ε₀, colliding beams",
  fixedThreshold: "KΣ,min/ε₀, fixed target",
  colliderThreshold: "KΣ,min/ε₀, colliding beams",
  cmKinetic: "Centre-of-momentum motion energy in S: (EΣ − W)/ε₀",
  allowed: "Kinematically allowed",
  forbidden: "Below threshold",
  thresholdCaution:
    "Allowed means enough energy and momentum; the model computes no reaction probability. At exact threshold the available phase space is zero.",
  angle: "Ray angle from +x",
  angleFrame: "Angle specified in",
  source: "Source rest frame S",
  receiver: "Receiver rest frame S′",
  sourceAngle: "θ in S",
  receiverAngle: "θ′ in S′",
  ratio: "f′/f₀",
  transverseSource: "Transverse in S",
  transverseReceiver: "Transverse in S′",
  dopplerSvg: "Aberration and Doppler factor",
  dopplerDesc:
    "Two separate direction circles show the same light ray in the source and receiver rest frames. Arrow lengths are normalized; the frequency ratio appears in the table.",
  dopplerNote:
    "S is the source rest frame; S′ moves at β along +x. Angles describe light propagation, not the direction back toward the source. Arrows show direction, not frequency or trajectory.",
  dopplerFormula: String.raw`$f'/f_0=\gamma_u(1-\beta\cos\theta)=1/[\gamma_u(1+\beta\cos\theta')]$. Transverse in S gives $\gamma_u$; transverse in S′ gives $1/\gamma_u$.`,
  nullNorm: "(ω′² − kₓ′² − kᵧ′²)/ω₀²",
};
export type RelativityKey = keyof typeof sv;
const prefix = (entries: Record<RelativityKey, string>) =>
  Object.fromEntries(
    Object.entries(entries).map(([key, value]) => [`teacher.prep.relativity.${key}`, value]),
  );
export const dict = { sv: prefix(sv), en: prefix(en) };
